import { getZonalStaffPresence } from 'api';
import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { useSelector } from 'react-redux';

const StaffAvailabilityChart = () => {
  const [chartData, setChartData] = useState([]);
  const [zones, setZones] = useState([]);
  const [isDataAvailable, setIsDataAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const date = useSelector((state) => state.customization.currentDate);
  const selectedTime = useSelector((state) => state.customization.selectedTime);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const formattedDate = formatDate(date);
  console.log('Formatted date:', formattedDate);
  console.log('Selected time:', selectedTime);

  // Convert time to 24-hour format (minutes from midnight)
  const convertTo24Hour = (time) => {
    if (!time || typeof time !== 'string') return null;
    let [hours, minutes] = time.trim().split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Function to format minutes into "HH:MM AM/PM"
  const formatTime = (minutes) => {
    if (minutes === null || isNaN(minutes)) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
    return `${formattedHours}:${mins.toString().padStart(2, '0')} ${ampm}`;
  };

  // const source = JSON.parse(localStorage.getItem('source'));
  const source = "trends";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        if (!formattedDate) {
          setIsDataAvailable(false);
          setIsLoading(false);
          return;
        }
        const startTime = new Date(selectedTime.start_time).toUTCString();
        const endTime = new Date(selectedTime.end_time).toUTCString();

        const requestData = {
          date: formattedDate,
          source_id: source,
          start_time: startTime,
          end_time: endTime
        };
        const response = await getZonalStaffPresence(requestData);

        if (response?.data?.data?.[0]?.staffAvailable?.length > 0) {
          const apiData = response.data.data[0].staffAvailable;
          const formattedZones = [];
          const seriesData = [];

          apiData.forEach((zoneItem) => {
            const { zone, staff } = zoneItem.zones;
            if (zone && staff.length > 0) {
              formattedZones.push(zone);
              staff.forEach(({ available_from, available_to }) => {
                const startTime = convertTo24Hour(available_from);
                const endTime = convertTo24Hour(available_to);

                if (startTime !== null && endTime !== null) {
                  seriesData.push({
                    x: zone,
                    y: [startTime, endTime]
                  });
                }
              });
            }
          });

          if (formattedZones.length > 0 && seriesData.length > 0) {
            setZones(formattedZones);
            setChartData([{ name: 'Staff Availability', data: seriesData }]);
            setIsDataAvailable(true);
            setIsLoading(false); // Fixed: Set loading to false when data is available
          } else {
            setIsDataAvailable(false);
            setIsLoading(false);
          }
        } else {
          setIsDataAvailable(false);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching staff availability data:', error);
        setIsDataAvailable(false);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [date, formattedDate, selectedTime?.start_time_string, selectedTime?.end_time_string]);

  const options = {
    chart: {
      type: 'rangeBar',
      height: 400,
      toolbar: { show: false },
      animations: {
        enabled: isDataAvailable
      }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '80%',
        borderRadius: 4
      }
    },
    fill: {
      type: 'solid',
      opacity: 0.8
    },
    xaxis: {
      type: 'numeric',
      title: { text: 'Time' },
      labels: { formatter: formatTime }
    },
    yaxis: {
      title: { text: 'Departments' },
      categories: zones
    },
    tooltip: {
      y: {
        formatter: (value) => (Array.isArray(value) ? `${formatTime(value[0])} - ${formatTime(value[1])}` : formatTime(value))
      }
    },
    legend: {
      position: 'top'
    },
    colors: ['#44BE54'],
    noData: {
      text: 'No staff availability data available',
      align: 'center',
      verticalAlign: 'middle',
      style: {
        color: '#6c757d',
        fontSize: '16px'
      }
    }
  };

  if (!date) {
    return (
      <div className="flex flex-col items-center justify-center p-4 bg-white rounded-lg shadow">
        <div className="text-lg font-medium text-gray-700 mb-2">Please select a date to view staff availability</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="chart-loading-container"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '480px',
          flexDirection: 'column',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px'
        }}
      >
        <div
          className="spinner"
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '50%',
            borderLeftColor: '#44BE54',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}
        ></div>
        <style>{`
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
        <div style={{ fontWeight: '500', color: '#555' }}>Loading Staff Availability Data...</div>
      </div>
    );
  }

  if (!isDataAvailable) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '480px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px'
        }}
      >
        <p style={{ color: '#6c757d', fontSize: '16px', marginBottom: '8px' }}>
          No staff availability data is available for {formattedDate}
        </p>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <Chart options={options} series={chartData} type="rangeBar" height={480} />
    </div>
  );
};

export default StaffAvailabilityChart;